import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { collection, limit, orderBy, query, where } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { db } from "../../firebase";
import { useSession } from "../../auth/AuthProvider";
import { useLiveQuery } from "../../hooks/useLive";
import { formatDate, quotaEntries } from "../../lib/licenses";
import { PRODUCTS } from "../../config";
import {
  customerCountQuery,
  distributorKeyCountQuery,
} from "../../lib/queries";
import { useCounts } from "../../hooks/usePaged";
import {
  addPortalUser,
  inviteMessage,
  removeUser,
  sendInvite,
} from "../../lib/operations";
import { figureSx } from "../../theme";
import {
  ConfirmDialog,
  EmptyState,
  Field,
  Loading,
  PageHeader,
  QuotaMeter,
  SectionTitle,
} from "../../components/ui";
import ActivityList from "../../components/ActivityList";
import DistributorKeys from "../../components/DistributorKeys";
import IssueKeyDialog from "../../components/IssueKeyDialog";
import AllocateDialog from "./AllocateDialog";
import DistributorDialog from "./DistributorDialog";
import { useAdminData } from "./AdminData";

function QuotaCard({ entry, onAdjust }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2">{entry.productName}</Typography>
        <Typography variant="caption" color="text.secondary">
          {entry.type}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 1 }}>
          <Typography sx={{ ...figureSx, fontSize: "2rem" }}>
            {entry.remaining}
          </Typography>
          <Typography color="text.secondary">
            left of {entry.allocated}
          </Typography>
        </Box>
        <QuotaMeter allocated={entry.allocated} issued={entry.issued} />
        <Button
          size="small"
          sx={{ mt: 1, ml: -1 }}
          onClick={() => onAdjust(entry)}
        >
          Adjust
        </Button>
      </CardContent>
    </Card>
  );
}

// Sends the Firebase sign-in email, or copies an invite to paste into
// WhatsApp / your own email.
function InviteButtons({ email, distributorName }) {
  const [state, setState] = useState(null); // null | "sending" | "sent" | "copied" | "error"

  async function send() {
    setState("sending");
    try {
      await sendInvite(email);
      setState("sent");
    } catch (e) {
      console.error(e);
      setState("error");
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        inviteMessage({ email, distributorName }),
      );
      setState("copied");
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
    >
      <Button size="small" onClick={send} disabled={state === "sending"}>
        {state === "sent"
          ? "Link sent"
          : state === "sending"
            ? "Sending…"
            : "Email sign-in link"}
      </Button>
      <Button size="small" color="inherit" onClick={copy}>
        {state === "copied" ? "Copied" : "Copy invite"}
      </Button>
      {state === "error" && (
        <Typography variant="caption" color="error">
          Couldn't send. Is email-link sign-in enabled?
        </Typography>
      )}
    </Box>
  );
}

function PortalUsers({ distributor, users }) {
  const session = useSession();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [justAdded, setJustAdded] = useState(null);

  async function add(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addPortalUser({
        email,
        distributorId: distributor.id,
        actor: session.email,
      });
      setJustAdded(email.trim().toLowerCase());
      setEmail("");
    } catch (e) {
      setError(e.message || "Couldn't add that login.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    setBusy(true);
    try {
      await removeUser({
        email: removing,
        distributorId: distributor.id,
        actor: session.email,
      });
      setRemoving(null);
    } catch (e) {
      setError(e.message || "Couldn't remove that login.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add the email of each person who should issue keys, then send them the
        invite. There is no password: they sign in with Google (Gmail or Google
        Workspace addresses) or with a link emailed to them, and their account
        is created the first time they sign in.
      </Typography>
      {users.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Nobody can sign in for {distributor.name} yet.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {users.map((u) => (
            <Box
              key={u.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  {u.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  added {formatDate(u.addedAt)}
                </Typography>
              </Box>
              <InviteButtons email={u.id} distributorName={distributor.name} />
              <Tooltip title="Remove access">
                <IconButton
                  size="small"
                  onClick={() => setRemoving(u.id)}
                  aria-label={`Remove ${u.id}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
      <Box component="form" onSubmit={add} sx={{ display: "flex", gap: 1 }}>
        <TextField
          type="email"
          placeholder="name@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ flex: 1 }}
          required
        />
        <Button
          type="submit"
          variant="outlined"
          color="inherit"
          disabled={busy || !email.trim()}
        >
          Add login
        </Button>
      </Box>
      {justAdded && (
        <Alert
          severity="success"
          sx={{ mt: 1.5 }}
          onClose={() => setJustAdded(null)}
        >
          {justAdded} can now sign in at {window.location.origin}. Send them the
          invite with the buttons next to their address.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      <ConfirmDialog
        open={!!removing}
        title="Remove access?"
        body={`${removing} will no longer be able to sign in for ${distributor.name}. Keys they issued stay valid.`}
        confirmLabel="Remove"
        destructive
        busy={busy}
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
      />
    </Paper>
  );
}

export default function DistributorDetailPage() {
  const { distributorId } = useParams();
  const session = useSession();
  const { distributorById, distributorsLoading, users } = useAdminData();
  const distributor = distributorById[distributorId];
  const [editing, setEditing] = useState(false);
  const [allocating, setAllocating] = useState(null); // null | {} | entry
  const [issuing, setIssuing] = useState(false);

  const activity = useLiveQuery(`activity/${distributorId}`, () =>
    query(
      collection(db, "activity"),
      where("distributorId", "==", distributorId),
      orderBy("at", "desc"),
      limit(30),
    ),
  );

  // Totals counted by Firestore.
  const countDefs = { customers: () => customerCountQuery(distributorId) };
  PRODUCTS.forEach((p) => {
    countDefs[`issued|${p.id}`] = () =>
      distributorKeyCountQuery(p.id, distributorId);
    countDefs[`used|${p.id}`] = () =>
      distributorKeyCountQuery(p.id, distributorId, [
        where("status", "==", "used"),
      ]);
  });
  const { counts } = useCounts(`detail|${distributorId}|${issuing}`, countDefs);
  const sum = (kind) =>
    PRODUCTS.every((p) => counts[`${kind}|${p.id}`] !== undefined)
      ? PRODUCTS.reduce((total, p) => total + counts[`${kind}|${p.id}`], 0)
      : "…";
  const logins = users.filter(
    (u) => u.role === "distributor" && u.distributorId === distributorId,
  );

  if (distributorsLoading) return <Loading />;
  if (!distributor) {
    return (
      <EmptyState
        title="Distributor not found"
        action={
          <Button component={RouterLink} to="/distributors">
            Back to distributors
          </Button>
        }
      />
    );
  }

  const entries = quotaEntries(distributor);

  return (
    <>
      <Link
        component={RouterLink}
        to="/distributors"
        underline="hover"
        color="text.secondary"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mb: 2,
          fontSize: 14,
        }}
      >
        <ArrowBackIcon fontSize="inherit" /> Distributors
      </Link>
      <PageHeader
        eyebrow={distributor.region}
        title={
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            {distributor.name}
            {distributor.active === false && (
              <Chip label="Paused" size="small" />
            )}
          </Box>
        }
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setEditing(true)}
              sx={{ borderColor: "divider" }}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIssuing(true)}
              sx={{ borderColor: "divider" }}
            >
              Issue a key
            </Button>
            <Button variant="contained" onClick={() => setAllocating({})}>
              Allocate keys
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" },
        }}
      >
        <Field label="Contact">{distributor.contactName}</Field>
        <Field label="Email">{distributor.contactEmail}</Field>
        <Field label="Phone">{distributor.phone}</Field>
        <Field label="Keys activated">{`${sum("used")} of ${sum("issued")} issued`}</Field>
        <Field label="Customer records">
          <Link
            component={RouterLink}
            to={`/customers?distributor=${distributor.id}`}
            underline="hover"
          >
            {counts.customers ?? "…"} customers
          </Link>
        </Field>
      </Box>

      <SectionTitle>Quota</SectionTitle>
      {entries.length === 0 ? (
        <EmptyState
          title="No keys allocated"
          body="Allocate a number of keys per product and type. Nothing is generated until the distributor issues a key to a customer."
          action={
            <Button variant="contained" onClick={() => setAllocating({})}>
              Allocate keys
            </Button>
          }
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          }}
        >
          {entries.map((entry) => (
            <QuotaCard
              key={entry.bucket}
              entry={entry}
              onAdjust={setAllocating}
            />
          ))}
        </Box>
      )}

      <SectionTitle>Portal logins</SectionTitle>
      <PortalUsers distributor={distributor} users={logins} />

      <SectionTitle>Issued keys</SectionTitle>
      <DistributorKeys
        distributorId={distributor.id}
        canManage
        actor={session.email}
        exportName={distributor.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}
      />

      <SectionTitle>Activity</SectionTitle>
      {activity.error ? (
        <Alert severity="warning">
          Activity needs a Firestore index — run{" "}
          <code>firebase deploy --only firestore:indexes</code>.
        </Alert>
      ) : (
        <ActivityList entries={activity.data} />
      )}

      <DistributorDialog
        open={editing}
        distributor={distributor}
        onClose={() => setEditing(false)}
      />
      <AllocateDialog
        open={!!allocating}
        distributor={distributor}
        initial={allocating?.bucket ? allocating : null}
        onClose={() => setAllocating(null)}
      />
      <IssueKeyDialog
        open={issuing}
        distributor={distributor}
        onBehalf
        onClose={() => setIssuing(false)}
      />
    </>
  );
}
